import { useMemo, useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { Textarea } from "../ui/textarea";
import {
  WorkbenchPage,
  WorkbenchPageContent,
  WorkbenchPageHeader,
} from "../ui/workbench-page";
import type { Book } from "../../types";
import { cn } from "../../lib/utils";
import {
  ensurePlotGridState,
  generatePlotThreadId,
  getPlotGrid,
  seedPlotGridFromChapters,
  setPlotCell,
  setPlotThread,
  type PlotCell,
} from "../../../shared/plotGrid";
import { IconLayoutGrid } from "./icons";

const INTENSITY_CLASS: Record<0 | 1 | 2 | 3, string> = {
  0: "bg-transparent",
  1: "bg-primary/20",
  2: "bg-primary/45",
  3: "bg-primary/70",
};

function cellKey(threadId: string, chapterId: string): string {
  return `${threadId}::${chapterId}`;
}

function nextIntensity(current: 0 | 1 | 2 | 3): 0 | 1 | 2 | 3 {
  return ((current + 1) % 4) as 0 | 1 | 2 | 3;
}

export function PlotGridPanel({
  book,
  onBookChange,
}: {
  book: Book;
  onBookChange: (book: Book) => void;
}) {
  const grid = useMemo(() => {
    ensurePlotGridState(book);
    return getPlotGrid(book);
  }, [book]);

  const chapters = useMemo(
    () =>
      [...(Array.isArray(book.chapters) ? book.chapters : [])].sort(
        (a, b) => (a.order ?? 0) - (b.order ?? 0)
      ),
    [book.chapters]
  );

  const cellMap = useMemo(() => {
    const map = new Map<string, PlotCell>();
    for (const cell of grid.cells) {
      map.set(cellKey(cell.threadId, cell.chapterId), cell);
    }
    return map;
  }, [grid.cells]);

  const [selected, setSelected] = useState<{ threadId: string; chapterId: string } | null>(null);
  const [newThreadName, setNewThreadName] = useState("");

  const selectedCell = selected
    ? cellMap.get(cellKey(selected.threadId, selected.chapterId)) ?? {
        threadId: selected.threadId,
        chapterId: selected.chapterId,
        note: "",
        intensity: 0 as const,
      }
    : null;

  function cycleCell(threadId: string, chapterId: string): void {
    const current = cellMap.get(cellKey(threadId, chapterId));
    const intensity = nextIntensity(current?.intensity ?? 0);
    const next = setPlotCell(book, {
      threadId,
      chapterId,
      note: current?.note ?? "",
      intensity,
    });
    setSelected({ threadId, chapterId });
    onBookChange(next);
  }

  function updateSelectedNote(note: string): void {
    if (!selected) return;
    const current = cellMap.get(cellKey(selected.threadId, selected.chapterId));
    onBookChange(
      setPlotCell(book, {
        threadId: selected.threadId,
        chapterId: selected.chapterId,
        note,
        intensity: current?.intensity ?? 0,
      })
    );
  }

  function addThread(): void {
    const name = newThreadName.trim() || `Thread ${grid.threads.length + 1}`;
    const thread = {
      id: generatePlotThreadId(),
      name,
    };
    setNewThreadName("");
    onBookChange(setPlotThread(book, thread));
  }

  function renameThread(threadId: string, name: string): void {
    const existing = grid.threads.find((t) => t.id === threadId);
    if (!existing) return;
    onBookChange(setPlotThread(book, { ...existing, name }));
  }

  function seedFromChapters(): void {
    onBookChange(seedPlotGridFromChapters(book));
  }

  return (
    <section className="h-full min-h-0" aria-label="Plot grid">
      <WorkbenchPage>
        <WorkbenchPageHeader
          title={
            <span className="flex items-center gap-2">
              <span className="text-muted-foreground" aria-hidden>
                <IconLayoutGrid size={18} />
              </span>
              Plot grid
            </span>
          }
          description="Threads as rows, chapters as columns. Click a cell to cycle intensity (0–3)."
          actions={
            <>
            <Input
              aria-label="New thread name"
              className="w-40"
              placeholder="Thread name"
              value={newThreadName}
              onChange={(e) => setNewThreadName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addThread();
                }
              }}
            />
            <Button size="sm" type="button" onClick={addThread}>
              Add thread
            </Button>
            {grid.threads.length === 0 ? (
              <Button size="sm" type="button" variant="secondary" onClick={seedFromChapters}>
                Seed from tags
              </Button>
            ) : null}
            </>
          }
        />

        <WorkbenchPageContent>
          <div className="flex flex-col gap-2.5">
          {grid.threads.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              No plot threads yet. Add a thread or seed from chapter tags and summaries.
            </p>
          ) : chapters.length === 0 ? (
            <p className="text-xs text-muted-foreground">Add chapters to fill the grid.</p>
          ) : (
            <div className="max-h-[min(68vh,540px)] overflow-auto rounded-lg border border-border/50 bg-card shadow-inner">
              <table
                className="w-full border-separate border-spacing-0 text-left"
                aria-label="Plot intensity matrix"
              >
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead
                      className="sticky top-0 left-0 z-40 h-auto min-w-38 border-r border-b border-border/50 bg-muted px-2 py-1.5 text-[0.6875rem] font-semibold tracking-[0.01em] text-foreground"
                      scope="col"
                    >
                      Thread
                    </TableHead>
                    {chapters.map((chapter) => (
                      <TableHead
                        key={chapter.id}
                        className="sticky top-0 z-20 h-auto min-w-13 max-w-19 border-b border-border/50 bg-muted px-2 py-1.5 text-center text-[0.6875rem] font-semibold tracking-[0.01em] text-muted-foreground"
                        scope="col"
                        title={chapter.title}
                      >
                        <span className="block overflow-hidden text-ellipsis whitespace-nowrap">
                          {chapter.chapterNumber ?? chapter.order}.{" "}
                          {chapter.title.length > 10
                            ? `${chapter.title.slice(0, 9)}…`
                            : chapter.title}
                        </span>
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {grid.threads.map((thread) => (
                    <TableRow key={thread.id} className="hover:bg-transparent">
                      <TableHead
                        className="sticky left-0 z-10 h-auto min-w-38 border-r border-b border-border/40 bg-card px-2 py-1 align-middle"
                        scope="row"
                      >
                        <Input
                          aria-label={`Rename ${thread.name}`}
                          className="h-7 text-xs"
                          value={thread.name}
                          onChange={(e) => renameThread(thread.id, e.target.value)}
                        />
                      </TableHead>
                      {chapters.map((chapter) => {
                        const cell = cellMap.get(cellKey(thread.id, chapter.id));
                        const intensity = cell?.intensity ?? 0;
                        const isSelected =
                          selected?.threadId === thread.id && selected?.chapterId === chapter.id;
                        return (
                          <TableCell
                            key={chapter.id}
                            className="border-b border-border/35 p-0.5 align-middle"
                          >
                            <Button
                              type="button"
                              variant="ghost"
                              className={cn(
                                "flex h-7.5 w-full min-w-10.5 cursor-pointer items-center justify-center rounded-md border border-transparent p-0 text-[0.6875rem] font-semibold text-foreground tabular-nums transition-[border-color,box-shadow,transform] hover:border-primary/35 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50",
                                INTENSITY_CLASS[intensity],
                                isSelected && "border-primary/70 ring-2 ring-primary/20"
                              )}
                              title={
                                cell?.note
                                  ? `${intensity}/3 — ${cell.note}`
                                  : `Intensity ${intensity}/3`
                              }
                              aria-label={`${thread.name} × ${chapter.title}: intensity ${intensity}`}
                              aria-pressed={isSelected}
                              onClick={() => cycleCell(thread.id, chapter.id)}
                            >
                              {intensity > 0 ? intensity : ""}
                            </Button>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </table>
            </div>
          )}

          {selectedCell && selected ? (
            <div className="flex flex-col gap-1 rounded-lg border border-border/60 bg-card px-3.5 py-3">
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-medium">
                  Cell note · intensity {selectedCell.intensity}/3
                </span>
                <Button size="sm" variant="secondary" type="button" onClick={() => setSelected(null)}>
                  Close
                </Button>
              </div>
              <Textarea
                aria-label="Plot cell note"
                rows={3}
                placeholder="What happens on this thread in this chapter…"
                value={selectedCell.note}
                onChange={(e) => updateSelectedNote(e.target.value)}
              />
            </div>
          ) : null}
          </div>
        </WorkbenchPageContent>
      </WorkbenchPage>
    </section>
  );
}
